import React from "react";
import { observable } from "mobx";
import { uniq, orderBy } from "lodash";
import {
  startOfMonth,
  subDays,
  differenceInDays,
  endOfMonth,
  subMonths,
} from "date-fns";
import { Formatter } from "libs";
import * as Api from "api";
import * as AdReport from "api/agencies/AdReport";
import * as RevenueReport from "api/agencies/RevenueReport";

export class DestinationStore {
  @observable.ref
  public loading = false;

  @observable
  public days = {
    selected: 0,
    options: new Array<{ label: string; date: Date; days: number }>(),
  };

  @observable.ref
  public adReports: AdReport.IndexResponse[] = [];

  @observable.ref
  public revenueReports: RevenueReport.IndexResponse[] = [];

  public constructor() {
    const yesterday = subDays(new Date(), 1);
    const thisMonth =
      differenceInDays(new Date(), startOfMonth(new Date())) - 1;
    const lastMonth = endOfMonth(subMonths(new Date(), 1));

    this.days.options = [
      ...uniq(orderBy([6, 13, 20, thisMonth])).map((days) => ({
        label: thisMonth === days ? "今月" : `${days + 1}日間`,
        date: yesterday,
        days: days,
      })),
      {
        label: "先月",
        date: lastMonth,
        days: differenceInDays(lastMonth, startOfMonth(lastMonth)),
      },
    ];

    this.days.selected = this.days.options.findIndex(
      (d) => d.days === thisMonth
    );
  }

  public async fetch(agencyId: number, appId: number) {
    this.loading = true;
    await Promise.all([
      this.fetchAdReports(agencyId, appId),
      this.fetchRevenueReport(agencyId, appId),
    ]);
    this.loading = false;
  }

  private async fetchAdReports(agencyId: number, appId: number) {
    const option = this.days.options[this.days.selected];
    const response = await AdReport.Api.index(agencyId, {
      appId,
      startAt: Formatter.date(subDays(option.date, option.days)),
      endAt: Formatter.date(option.date),
    });

    if (response?.status === Api.Status.OK) {
      this.adReports = response.data;
    }
  }

  private async fetchRevenueReport(agencyId: number, appId: number) {
    const option = this.days.options[this.days.selected];
    const response = await RevenueReport.Api.index(agencyId, {
      appId,
      startAt: Formatter.date(subDays(option.date, option.days)),
      endAt: Formatter.date(option.date),
    });

    if (response?.status === Api.Status.OK) {
      this.revenueReports = response.data;
    }
  }
}

const Context = React.createContext<DestinationStore | null>(null);

export const DestinationStoreProvider: React.FC<{
  value?: DestinationStore;
}> = ({ children, value }) => {
  const store = React.useMemo(() => (value ? value : new DestinationStore()), [
    value,
  ]);
  return <Context.Provider value={store}>{children}</Context.Provider>;
};

export const useDestinationStore = () => {
  const store = React.useContext(Context);
  if (!store) {
    throw new Error(
      "useDestinationStore must be used within a DestinationStoreProvider."
    );
  }
  return store;
};
